import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Node, useReactFlow, useStoreApi } from "@xyflow/react";
import { Alert, Button, Col, Divider, Drawer, Form, FormInstance, Input, Modal, notification, Row, Select, Space, Switch, Tag, Typography } from "antd";
import { v4 as uuidv4 } from "uuid";
import { DeleteOutlined, ReloadOutlined } from "@ant-design/icons";

import { EItemComparisonType, EItemFormatType, EItemType, ICondition, IItemComparison, IItemField } from "../../../SimuladorInteligente/interfaces";
import { CustomNode, IDefaultInputInstruction, IDefaultMessage } from "../../interfaces";

import currency from "../../../SimuladorInteligente/utils/masks/currency";

interface IProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (values: Node) => void;
  nodeToEdit?: CustomNode | null;
  defaultMessages: IDefaultMessage[];
  defaultInputInstructions: IDefaultInputInstruction[];
}

const INITIAL_TYPE = EItemType.MESSAGE;

const INPUT_FORMAT_OPTIONS = [
  {
    value: "currency",
    label: "Monetário (R$)",
  },
  {
    value: "cpf",
    label: "CPF",
  },
  {
    value: "cnpj",
    label: "CNPJ",
  },
  {
    value: "cellphone",
    label: "Telefone",
  },
  {
    value: "email",
    label: "Email",
  },
]

const INPUT_TYPE_OPTIONS = [
  {
    value: "text",
    label: "Texto",
  },
  {
    value: "email",
    label: "Email",
  },
]

export default function NodeFormDrawer({
  visible,
  onClose,
  onSubmit,
  nodeToEdit,
  defaultInputInstructions,
  defaultMessages,
}: IProps) {
  const defaultType = nodeToEdit?.type as EItemType || INITIAL_TYPE;

  const [selectedType, setSelectedType] = useState<EItemType>(defaultType);
  const [conditionType, setConditionType] = useState<string | null>(null);
  const [valuesFrom, setValuesFrom] = useState<string[]>(nodeToEdit?.data.values_from || []);
  const [selectedComparisonItem, setSelectedComparisonItem] = useState<string | null>(null);
  const [optionsList, setOptionsList] = useState<{ text: string, description?: string }[]>(nodeToEdit?.data.options || []);
  const [fieldsList, setFieldsList] = useState<IItemField[]>(nodeToEdit?.data.fields || []);
  const [conditionsList, setConditionsList] = useState<ICondition[]>([]);
  const [comparisonsList, setComparisonsList] = useState<IItemComparison[]>(nodeToEdit?.data.comparisons || []);
  const [isConditionModalVisible, setIsConditionModalVisible] = useState(false);
  const [isOptionModalVisible, setIsOptionModalVisible] = useState(false);
  const [willUseDefaultMessage, setWillUseDefaultMessage] = useState(nodeToEdit?.data.default_message ? true : false);
  const [willUseDefaultInputInstructions, setWillUseDefaultInputInstructions] = useState(nodeToEdit?.data.default_input_instructions ? true : false);
  const [willLinkComparisonToLineOfCredit, setWillLinkComparisonToLineOfCredit] = useState(false);
  const [isInitialItem, setIsInitialItem] = useState(nodeToEdit?.data.is_initial_item || false);
  const [isMultiple, setIsMultiple] = useState(nodeToEdit?.data.allow_multiple_options || false);

  const itemFormRef = useRef<FormInstance>(null);
  const optionFormRef = useRef<FormInstance>(null);
  const fieldFormRef = useRef<FormInstance>(null);
  const comparisonFormRef = useRef<FormInstance>(null);
  const conditionFormRef = useRef<FormInstance>(null);

  const store = useStoreApi();
  const { screenToFlowPosition, getNodes } = useReactFlow();

  const nodes: CustomNode[] = useMemo(() => {
    return getNodes();
  }, [visible]);

  const hasInitialItem = useMemo(() => {
    return !!nodes.find((e) => e.data.is_initial_item);
  }, [nodes]);

  const lineOfCreditNodes: CustomNode[] = useMemo(() => {
    return nodes.filter((e) => e.data.table_display_name === "Linha de crédito");
  }, [nodes]);

  const resetValues = useCallback(() => {
    itemFormRef.current?.resetFields();
    optionFormRef.current?.resetFields();
    comparisonFormRef.current?.resetFields();
    conditionFormRef.current?.resetFields();

    setSelectedType(defaultType);
    setConditionType(null);
    setValuesFrom([]);
    setSelectedComparisonItem(null);
    setOptionsList([]);
    setConditionsList([]);
    setComparisonsList([]);
    setIsConditionModalVisible(false);
    setIsOptionModalVisible(false);
    setWillUseDefaultMessage(false);
    setWillUseDefaultInputInstructions(false);
    setIsInitialItem(false);
    setIsMultiple(false);
    setWillLinkComparisonToLineOfCredit(false);
  }, []);

  const getCenterPosition = () => {
    const { domNode } = store.getState();
    const boundingRect = domNode?.getBoundingClientRect();

    if (boundingRect) {

      const center = screenToFlowPosition({
        x: boundingRect.x + boundingRect.width / 2,
        y: boundingRect.y + boundingRect.height / 2,
      });

      return center;
    }

    return null;
  }

  const getNodeToEditCurrentPosition = () => {
    if (!nodeToEdit) {
      return null;
    }

    return nodeToEdit?.position || {
      x: nodeToEdit?.positionAbsoluteX,
      y: nodeToEdit?.positionAbsoluteY,
    }
  }

  const orderedComparisonsList = useMemo(() => {
    return comparisonsList.sort((a, b) => (a.order || 0) - (b.order || 0))
  }, [comparisonsList]);

  const showUpdateOrdersButton = useMemo(() => {
    return comparisonsList.some((e) => !!e.line_of_credit_id);
  }, [comparisonsList]);

  const handleSubmit = useCallback((values: any) => {
    const id = nodeToEdit?.id || uuidv4();

    const position = getNodeToEditCurrentPosition() || getCenterPosition();

    const data: {
      label: string;
      message?: string;
      options?: { text: string }[];
      comparisons?: IItemComparison[];
      format?: EItemFormatType;
      placeholder?: string;
      line_of_credit?: string;
      fields?: IItemField[];
      values_from?: string[];
      input_instructions?: string;
      default_message?: string;
      default_input_instructions?: string;
      is_initial_item: boolean;
      allow_multiple_options?: boolean;
      table_display_name?: string;
      link_text?: string;
      link?: string;
      order?: number;
      currency_min_value?: number | null;
      currency_max_value?: number | null;
    } = {
      label: `Node ${id}`,
      message: !willUseDefaultMessage ? values.message : undefined,
      is_initial_item: isInitialItem,
      allow_multiple_options: values.type === EItemType.OPTIONS ? isMultiple : undefined,
    };

    if (willUseDefaultMessage) {
      data.default_message = values.default_message;
    }

    if ([EItemType.MESSAGE, EItemType.RESULT].includes(values.type) && values.link_text && values.link) {
      data.link_text = values.link_text;
      data.link = values.link;
    }

    if (willUseDefaultInputInstructions) {
      data.default_input_instructions = values.default_input_instructions;
    }

    if ([EItemType.FIELD, EItemType.COLLECT, EItemType.OPTIONS].includes(values.type)) {
      data.input_instructions = !willUseDefaultInputInstructions ? values.input_instructions : undefined;
    }

    if (![EItemType.MESSAGE, EItemType.COMPARISON].includes(values.type)) {
      data.table_display_name = values.table_display_name;
    }

    if (values.type === EItemType.FIELD) {
      data.format = values.format;
      data.placeholder = values.placeholder;

      if (values.format === EItemFormatType.CURRENCY) {
        data.currency_min_value = values.currency_min_value ? currency.unMask(values.currency_min_value) || null : null;
        data.currency_max_value = values.currency_max_value ? currency.unMask(values.currency_max_value) || null : null;
      }
    }

    if (values.type === EItemType.COLLECT) {
      data.fields = fieldsList;
    }

    if (values.type === EItemType.RESULT) {
      data.line_of_credit = values.line_of_credit;
    }

    if (values.type === EItemType.OPTIONS) {
      data.options = optionsList;
    }

    if (values.type === EItemType.COMPARISON) {
      data.comparisons = orderedComparisonsList;
      data.values_from = values.values_from;
    }

    if (values.order) {
      const orderValue = values.order;

      if (typeof orderValue === "string") {
        data.order = Number(values.order.replace(",", "."));
      }

      if (typeof orderValue === "number") {
        data.order = orderValue;
      }
    }

    const node: Node = {
      id,
      position: {
        x: position?.x || 0,
        y: position?.y || 0,
      },
      data,
      type: values.type,
      selected: true,
    };

    onSubmit(node);
    onClose();
    resetValues();
  }, [
    optionsList,
    orderedComparisonsList,
    fieldsList,
    willUseDefaultMessage,
    willUseDefaultInputInstructions,
    isInitialItem,
    isMultiple,
    onSubmit
  ]);

  const handleAddOption = useCallback((values: any) => {
    setOptionsList((prevState) => prevState.concat({ text: values.option, description: values.description }));

    setIsOptionModalVisible(false);
    optionFormRef.current?.resetFields();
  }, []);

  const handleAddField = useCallback((values: any) => {
    if (values.format === EItemFormatType.CURRENCY) {
      values.currency_min_value = values.currency_min_value ? currency.unMask(values.currency_min_value) || null : null;
      values.currency_max_value = values.currency_max_value ? currency.unMask(values.currency_max_value) || null : null;
    }

    setFieldsList((prevState) => prevState.concat(values));

    fieldFormRef.current?.resetFields();
  }, []);

  const handleAddComparison = useCallback((values: any) => {
    const linhaDeCredito = values.line_of_credit_id ? lineOfCreditNodes.find((e) => e.id === values.line_of_credit_id) : undefined;

    setComparisonsList((prevState) => prevState.concat({
      id: uuidv4(),
      title: linhaDeCredito?.data.line_of_credit || values.title,
      order: linhaDeCredito?.data.order,
      line_of_credit_id: values.line_of_credit_id || undefined,
      conditions: conditionsList,
      next: ''
    }));

    conditionFormRef.current?.resetFields();
    comparisonFormRef.current?.resetFields();
    setConditionsList([]);
  }, [lineOfCreditNodes, conditionsList]);

  const handleAddCondition = useCallback((values: any) => {
    const formattedCondition: ICondition = {
      field: values.field,
      type: values.type,
    }

    if (values.type === EItemComparisonType.VALUES) {
      formattedCondition.values = values.values;
    }

    if (values.type === EItemComparisonType.NUMBER_INTERVAL) {
      formattedCondition.min_value = Number(values.min_value);
      formattedCondition.max_value = Number(values.max_value);
    }

    setConditionsList((prevState) => prevState.concat(formattedCondition))

    setIsConditionModalVisible(false);

    setConditionType(null);
    conditionFormRef.current?.resetFields();
  }, []);

  const handleToggleWillUseDefaultMessage = useCallback(() => {
    setWillUseDefaultMessage(prevState => !prevState);

    itemFormRef.current?.setFieldsValue({ default_message: undefined });
    itemFormRef.current?.resetFields(["message"])
    itemFormRef.current?.setFieldsValue({ message: undefined });
  }, []);

  const handleToggleWillUseDefaultInputInstructions = useCallback(() => {
    setWillUseDefaultInputInstructions(prevState => !prevState);

    itemFormRef.current?.setFieldsValue({ default_input_instructions: undefined });
    itemFormRef.current?.resetFields(["input_instructions"])
    itemFormRef.current?.setFieldsValue({ input_instructions: undefined });
  }, []);

  const handleToggleWillLinkComparisonToLineOfCredit = useCallback(() => {
    setWillLinkComparisonToLineOfCredit(prevState => !prevState);

    comparisonFormRef.current?.setFieldsValue({ title: undefined, line_of_credit_id: undefined });
  }, []);

  const handleUpdateOrders = useCallback(() => {
    setComparisonsList((prevState) => {
      return prevState.map((e) => ({
        ...e,
        order: lineOfCreditNodes.find((l) => l.id === e.line_of_credit_id)?.data?.order || undefined,
      }));
    })

    notification.success({
      message: 'Ordens atualizadas com sucesso!',
      description:
        'A lista foi reordenada baseada nas novas ordens.',
      duration: 2
    });
  }, [lineOfCreditNodes]);

  const isEditing = useMemo(() => {
    return !!nodeToEdit;
  }, [nodeToEdit]);

  useEffect(() => {
    return () => {
      // se visible for true, estará fechando o drawer
      if (visible) {
        resetValues();
      }
    }
  }, [visible]);

  return (
    <Drawer
      title={`${!isEditing ? "Adicionar" : "Editar"} item`}
      onClose={onClose}
      visible={visible}
      width={500}
      bodyStyle={{
        paddingBottom: 80,
      }}
      destroyOnClose
      maskClosable={false}
      extra={
        <Space>
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            type="primary"
            htmlType='submit'
            form='itemForm'
          >
            {!isEditing ? "Adicionar" : "Salvar"}
          </Button>
        </Space>
      }
    >
      <Form
        ref={itemFormRef}
        name='itemForm'
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          type: defaultType,
          message: !nodeToEdit?.data.default_message ? nodeToEdit?.data.message : defaultMessages.find((m) => m._id === nodeToEdit.data.default_message)?.message,
          default_message: nodeToEdit?.data.default_message,
          line_of_credit: nodeToEdit?.data.line_of_credit,
          values_from: nodeToEdit?.data.values_from,
          input_instructions: !nodeToEdit?.data.default_input_instructions ? nodeToEdit?.data.input_instructions : defaultInputInstructions.find((m) => m._id === nodeToEdit.data.default_input_instructions)?.message,
          default_input_instructions: nodeToEdit?.data.default_input_instructions,
          format: nodeToEdit?.data.format,
          placeholder: nodeToEdit?.data.placeholder,
          table_display_name: nodeToEdit?.data.table_display_name,
          link_text: nodeToEdit?.data.link_text,
          link: nodeToEdit?.data.link,
          order: nodeToEdit?.data.order,
          currency_min_value: nodeToEdit?.data.currency_min_value ? currency.mask(nodeToEdit?.data.currency_min_value * 100) : undefined,
          currency_max_value: nodeToEdit?.data.currency_max_value ? currency.mask(nodeToEdit?.data.currency_max_value * 100) : undefined,
        }}
      >
        {
          (!hasInitialItem || nodeToEdit?.data.is_initial_item) && (
            <Row gutter={16}>
              <Col
                span={24}
                style={{
                  marginBottom: 8,
                  display: 'flex',
                  justifyContent: 'flex-end'
                }}
              >
                <Switch
                  checked={isInitialItem}
                  onChange={setIsInitialItem}
                  checkedChildren="Item inicial do fluxo"
                  unCheckedChildren="Item inicial do fluxo"
                />
              </Col>
            </Row>
          )
        }

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="type"
              label="Tipo"
              rules={[{ required: true, message: 'Por favor, selecione um tipo' }]}
              style={(isEditing || selectedType === EItemType.COMPARISON) ? { marginBottom: 8 } : undefined}
            >
              <Select
                placeholder="Selecione"
                onChange={setSelectedType}
                disabled={isEditing}
              >
                <Select.Option value="message">Mensagem</Select.Option>
                <Select.Option value="collect">Coleta de dados</Select.Option>
                <Select.Option value="options">Opções</Select.Option>
                <Select.Option value="field" >Campo de digitação</Select.Option>
                <Select.Option value="comparison">Regras internas</Select.Option>
                <Select.Option value="result">Resultado</Select.Option>
              </Select>
            </Form.Item>

            {
              isEditing && (
                <Alert
                  message="Não é possível alterar o tipo de um item."
                  type="warning"
                  style={{
                    marginBottom: selectedType === EItemType.COMPARISON ? 8 : 24
                  }}
                />
              )
            }

            {
              selectedType === EItemType.COMPARISON && (
                <Alert
                  message="Este item não é visível para os usuários. Ele é utilizado para direcionar o fluxo baseado em respostas anteriores."
                  type="info"
                  style={{
                    marginBottom: 24
                  }}
                />
              )
            }
          </Col>
        </Row>

        {
          selectedType !== EItemType.COMPARISON && (
            <Row gutter={16}>
              {
                willUseDefaultMessage && (
                  <Col span={24}>
                    <Form.Item
                      name="default_message"
                      label="Mensagem pré-cadastrada"
                      rules={[{ required: true, message: 'Por favor, selecione a mensagem' }]}
                    >
                      <Select
                        placeholder="Selecione"
                        options={defaultMessages.map((message) => ({
                          label: message.title,
                          value: message._id,
                        }))}
                        onChange={((value) => itemFormRef.current?.setFieldsValue({ message: defaultMessages.find((m) => m._id === value)?.message }))}
                      />
                    </Form.Item>
                  </Col>
                )
              }

              <Col span={24}>
                <Form.Item
                  name="message"
                  label="Mensagem"
                  rules={!willUseDefaultMessage ? [
                    {
                      required: true,
                      message: 'Digite a mensagem',
                    },
                  ] : undefined}
                  style={{
                    marginBottom: 0,
                  }}
                >
                  <Input.TextArea
                    rows={4}
                    placeholder="Digite a mensagem"
                    disabled={willUseDefaultMessage}
                  />
                </Form.Item>
              </Col>

              <Col>
                <Button
                  type="link"
                  onClick={handleToggleWillUseDefaultMessage}
                  style={{
                    padding: 0,
                    marginBottom: 24,
                  }}
                >
                  {!willUseDefaultMessage ? "Vincular mensagem pré-cadastrada" : "Escrever mensagem customizada"}
                </Button>
              </Col>
            </Row>
          )
        }

        {
          ![EItemType.MESSAGE, EItemType.COMPARISON].includes(selectedType) && (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="table_display_name"
                  label="Nome para visualização"
                  rules={[
                    {
                      required: true,
                      message: 'Digite o nome para visualização',
                    },
                  ]}
                >
                  <Input placeholder="Digite o nome para visualização" />
                </Form.Item>
              </Col>
            </Row>
          )
        }

        {
          selectedType !== EItemType.COMPARISON && selectedType !== EItemType.MESSAGE && selectedType !== EItemType.RESULT && (
            <Row gutter={16}>
              {
                willUseDefaultInputInstructions && (
                  <Col span={24}>
                    <Form.Item
                      name="default_input_instructions"
                      label="Instrução pré-cadastrada"
                    >
                      <Select
                        placeholder="Selecione"
                        options={defaultInputInstructions.map((instruction) => ({
                          label: instruction.title,
                          value: instruction._id,
                        }))}
                        onChange={((value) => itemFormRef.current?.setFieldsValue({ input_instructions: value ? defaultInputInstructions.find((m) => m._id === value)?.message : undefined }))}
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                )
              }

              <Col span={24}>
                <Form.Item
                  name="input_instructions"
                  label="Instruções do campo de resposta"
                  style={{
                    marginBottom: 0,
                  }}
                >
                  <Input.TextArea
                    rows={4}
                    placeholder="Digite as instruções do campo de resposta"
                    disabled={willUseDefaultInputInstructions}
                  />
                </Form.Item>
              </Col>

              <Col>
                <Button
                  type="link"
                  onClick={handleToggleWillUseDefaultInputInstructions}
                  style={{
                    padding: 0,
                    marginBottom: 24,
                  }}
                >
                  {!willUseDefaultMessage ? "Vincular instrução pré-cadastrada" : "Escrever instrução customizada"}
                </Button>
              </Col>
            </Row>
          )
        }

        {
          selectedType === "result" && (
            <>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="line_of_credit"
                    label="Linha de crédito"
                    rules={[
                      {
                        required: true,
                        message: 'Digite a linha de crédito',
                      },
                    ]}
                  >
                    <Input placeholder="Digite a linha de crédito" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="order"
                    label="Ordem"
                  >
                    <Input placeholder="Digite a ordem" />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )
        }

        {
          [EItemType.MESSAGE, EItemType.RESULT].includes(selectedType) && (
            <>
              <Typography.Title level={4}>Link</Typography.Title>

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="link_text"
                    label="Texto do botão"
                  >
                    <Input placeholder="Digite o texto do botão" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="link"
                    label="Link do botão"
                  >
                    <Input placeholder="Cole o link do botão" />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )
        }

        {
          selectedType === "comparison" && (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="values_from"
                  label="Valores dos seguinte itens"
                  rules={[{ required: true, message: 'Por favor, selecione pelo menos um item' }]}
                >
                  <Select
                    placeholder="Selecione"
                    onChange={setValuesFrom}
                    mode="multiple"
                    options={nodes.filter((e) => e.type && ["options", "field"].includes(e.type)).map((e) => ({
                      label: e.data.message,
                      value: e.id,
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>
          )
        }

        {
          selectedType === "field" && (
            <>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="format"
                    label="Formatação"
                  >
                    <Select
                      placeholder="Selecione"
                      allowClear
                      options={INPUT_FORMAT_OPTIONS.map((opt) => ({
                        label: opt.label,
                        value: opt.value,
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => prevValues.format !== currentValues.format}
              >
                {({ getFieldValue }) =>
                  getFieldValue('format') === EItemFormatType.CURRENCY ? (
                    <>
                      <Row gutter={16}>
                        <Col span={24}>
                          <Form.Item
                            name="currency_min_value"
                            label="Valor mínimo"
                          >
                            <Input
                              placeholder={"Digite o valor mínimo"}
                              prefix={"R$"}
                              onChange={(e) => itemFormRef.current?.setFieldsValue({ currency_min_value: currency.mask(e.target.value) })}
                            />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={16}>
                        <Col span={24}>
                          <Form.Item
                            name="currency_max_value"
                            label="Valor máximo"
                          >
                            <Input
                              placeholder={"Digite o valor máximo"}
                              prefix={"R$"}
                              onChange={(e) => itemFormRef.current?.setFieldsValue({ currency_max_value: currency.mask(e.target.value) })}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </>
                  ) : null
                }
              </Form.Item>

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="placeholder"
                    label="Placeholder"
                    rules={[
                      {
                        required: true,
                        message: 'Digite o placeholder',
                      },
                    ]}
                  >
                    <Input placeholder="Digite o placeholder" />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )
        }
      </Form>

      {
        selectedType === "options" && (
          <>
            <Row gutter={16}>
              <Col
                span={24}
                style={{
                  marginBottom: 8,
                  display: 'flex',
                  justifyContent: 'flex-end'
                }}
              >
                <Switch
                  checked={isMultiple}
                  onChange={setIsMultiple}
                  checkedChildren="Permitir múltiplas opções"
                  unCheckedChildren="Permitir múltiplas opções"
                />
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Typography.Title level={4}>Opções</Typography.Title>
              </Col>

              <Col
                span={24}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  marginBottom: 16,
                }}
              >
                {
                  optionsList.map((option, index) => (
                    <Fragment key={option.text + index}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: "8px 0",
                          gap: 12,
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <Typography.Text>{option.text}</Typography.Text>
                          <Typography.Text type="secondary">{option.description}</Typography.Text>
                        </div>

                        <DeleteOutlined
                          onClick={() => setOptionsList((prev) => prev.filter((e) => e.text !== option.text))}
                        />
                      </div>

                      {
                        index < optionsList.length - 1 && <Divider style={{ margin: 0 }} />
                      }
                    </Fragment>
                  ))
                }
              </Col>

              <Col>
                <Button
                  onClick={() => setIsOptionModalVisible(true)}
                >
                  Nova opção
                </Button>
              </Col>
            </Row>
          </>
        )
      }

      {
        selectedType === "collect" && (
          <Form
            ref={fieldFormRef}
            name='fieldForm'
            layout="vertical"
            onFinish={handleAddField}
            onSubmitCapture={(e) => e.preventDefault()}
          >
            <Row gutter={16}>
              <Col span={24}>
                <Typography.Title level={4}>Campos</Typography.Title>
              </Col>

              <Col
                span={24}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  marginBottom: 16,
                }}
              >
                {
                  fieldsList.map((field, index) => (
                    <Fragment key={field.name}>
                      <div style={{ padding: "8px 0" }} >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Typography.Text key={field.name}>{field.label}</Typography.Text>

                          <DeleteOutlined
                            onClick={() => setFieldsList((prev) => prev.filter((e) => e.name !== field.name))}
                          />

                        </div>

                        <div
                          style={{
                            display: "flex",
                            flexDirection: 'column',
                          }}
                        >
                          <Typography.Text>Nome: {field.name}</Typography.Text>
                        </div>
                      </div>

                      {
                        index < fieldsList.length - 1 && <Divider style={{ margin: 0 }} />
                      }
                    </Fragment>
                  ))
                }
              </Col>

              <Col span={24} >
                <Form.Item
                  name="name"
                  label="Nome"
                  rules={[
                    {
                      required: true,
                      message: 'Digite o nome do campo',
                    },
                  ]}
                >
                  <div
                    style={{
                      display: 'flex',
                      // flexDirection: 'row',
                      alignItems: 'center',
                      gap: 16,
                    }}
                  >
                    <Input placeholder="Digite o nome do campo" />
                  </div>
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  name="label"
                  label="Label"
                  rules={[
                    {
                      required: true,
                      message: 'Digite a label do campo',
                    },
                  ]}
                >
                  <div
                    style={{
                      display: 'flex',
                      // flexDirection: 'row',
                      alignItems: 'center',
                      gap: 16,
                    }}
                  >
                    <Input placeholder="Digite a label do campo" />
                  </div>
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  name="placeholder"
                  label="Placeholder"
                  rules={[
                    {
                      required: true,
                      message: 'Digite o placeholder do campo',
                    },
                  ]}
                >
                  <div
                    style={{
                      display: 'flex',
                      // flexDirection: 'row',
                      alignItems: 'center',
                      gap: 16,
                    }}
                  >
                    <Input placeholder="Digite o placeholder do campo" />
                  </div>
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  name="type"
                  label="Tipo"
                  rules={[
                    {
                      required: true,
                      message: 'Selecione o tipo',
                    },
                  ]}
                >
                  <Select
                    placeholder="Selecione"
                    options={INPUT_TYPE_OPTIONS.map((opt) => ({
                      label: opt.label,
                      value: opt.value,
                    }))}
                  />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  name="format"
                  label="Formatação"
                >
                  <Select
                    placeholder="Selecione"
                    allowClear
                    options={INPUT_FORMAT_OPTIONS.map((opt) => ({
                      label: opt.label,
                      value: opt.value,
                    }))}
                  />
                </Form.Item>
              </Col>

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => prevValues.format !== currentValues.format}
              >
                {({ getFieldValue }) =>
                  getFieldValue('format') === EItemFormatType.CURRENCY ? (
                    <>
                      <Col span={24}>
                        <Form.Item
                          name="currency_min_value"
                          label="Valor mínimo"
                        >
                          <Input
                            placeholder={"Digite o valor mínimo"}
                            prefix={"R$"}
                            onChange={(e) => fieldFormRef.current?.setFieldsValue({ currency_min_value: currency.mask(e.target.value) })}
                          />
                        </Form.Item>
                      </Col>

                      <Col span={24}>
                        <Form.Item
                          name="currency_max_value"
                          label="Valor máximo"
                        >
                          <Input
                            placeholder={"Digite o valor máximo"}
                            prefix={"R$"}
                            onChange={(e) => fieldFormRef.current?.setFieldsValue({ currency_max_value: currency.mask(e.target.value) })}
                          />
                        </Form.Item>
                      </Col>
                    </>
                  ) : null
                }
              </Form.Item>

              <Button
                htmlType="submit"
                form="fieldForm"
              >
                Salvar campo
              </Button>
            </Row>
          </Form>
        )
      }

      {
        selectedType === "comparison" && (
          <Form
            ref={comparisonFormRef}
            name='comparisonForm'
            layout="vertical"
            onFinish={handleAddComparison}
            onSubmitCapture={(e) => e.preventDefault()}
          >
            <Row gutter={16}>
              <Col span={showUpdateOrdersButton ? 16 : 24}>
                <Typography.Title level={4}>Regras</Typography.Title>
              </Col>

              {
                showUpdateOrdersButton && (
                  <Col
                    span={8}
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Button
                      onClick={handleUpdateOrders}
                      type="link"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 8,
                        height: 'fit-content',
                        paddingRight: 0,
                      }}
                    >
                      <ReloadOutlined />

                      <Typography.Link>Atualizar ordens</Typography.Link>
                    </Button>
                  </Col>
                )
              }

              <Col
                span={24}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  marginBottom: 16,
                }}
              >
                {
                  orderedComparisonsList.map((comparison, index) => (
                    <Fragment key={comparison.id}>
                      <div style={{ padding: "8px 0" }} >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8
                            }}
                          >
                            <Typography.Text key={comparison.id}>{comparison.title}</Typography.Text>

                            {
                              comparison.order !== undefined && (
                                <Tag color="cyan">o/: {comparison.order}</Tag>
                              )
                            }
                          </div>

                          <DeleteOutlined
                            onClick={() => setComparisonsList((prev) => prev.filter((e) => e.id !== comparison.id))}
                          />
                        </div>

                        {
                          comparison.conditions.map((condition, conditionIndex) => (
                            <div
                              key={condition.field + conditionIndex}
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                marginLeft: 16
                              }}
                            >
                              <Typography.Text>
                                {nodes.find((e) => e.id === condition.field)?.data.message}
                              </Typography.Text>

                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  marginLeft: 16
                                }}
                              >
                                {
                                  condition.type === EItemComparisonType.VALUES ? (
                                    <>
                                      {
                                        condition.values?.map((value, valueIndex) => (
                                          <Typography.Text key={`${value}-${valueIndex}`}>- {value}</Typography.Text>
                                        ))
                                      }
                                    </>
                                  ) : (
                                    <Typography.Text>- Maior ou igual a R$ {currency.mask(Number(condition.min_value)?.toFixed(2))} e menor ou igual a R$ {currency.mask(Number(condition.max_value)?.toFixed(2))}</Typography.Text>
                                  )
                                }
                              </div>
                            </div>
                          ))
                        }
                      </div>

                      {
                        index < orderedComparisonsList.length - 1 && <Divider style={{ margin: 0 }} />
                      }
                    </Fragment>
                  ))
                }
              </Col>

              <Col span={24} >
                {
                  willLinkComparisonToLineOfCredit ? (
                    <Form.Item
                      name="line_of_credit_id"
                      label="Linha de crédito"
                      rules={[
                        {
                          required: true,
                          message: 'Selecione uma linha de crédito',
                        },
                      ]}
                      style={{
                        marginBottom: 0,
                      }}
                    >
                      <Select
                        placeholder="Selecione uma linha de crédito"
                        options={lineOfCreditNodes.map((e) => ({
                          label: e.data.line_of_credit,
                          value: e.id,
                        }))}
                      />
                    </Form.Item>
                  ) : (
                    <Form.Item
                      name="title"
                      label="Título"
                      rules={[
                        {
                          required: true,
                          message: 'Digite o título',
                        },
                      ]}
                      style={{
                        marginBottom: 0,
                      }}
                    >
                      <Input placeholder="Digite o título" />
                    </Form.Item>
                  )
                }
              </Col>

              <Col>
                <Button
                  type="link"
                  onClick={handleToggleWillLinkComparisonToLineOfCredit}
                  style={{
                    padding: 0,
                    marginBottom: 24,
                  }}
                >
                  {!willLinkComparisonToLineOfCredit ? "Vincular linha de crédito" : "Escrever condição sem vínculo a linhas de crédito"}
                </Button>
              </Col>

              <Col span={24}>
                <Typography.Title level={5}>Condições da regra</Typography.Title>
              </Col>

              <Col
                span={24}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  marginBottom: 16,
                }}
              >
                {
                  conditionsList.map((condition, index) => (
                    <Fragment key={condition.field + index}>
                      <div style={{ padding: "8px 0" }} >
                        {
                          condition.type === "values" && (
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                              }}
                            >
                              <Typography.Text key={condition.field}>{nodes.find((e) => e.id === condition.field)?.data.message}</Typography.Text>
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  marginLeft: 16
                                }}
                              >
                                <Typography.Text>- {condition.values?.join(', ')}</Typography.Text>
                              </div>
                            </div>
                          )
                        }

                        {
                          condition.type === "number_interval" && (
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                              }}
                            >
                              <Typography.Text key={condition.field}>{nodes.find((e) => e.id === condition.field)?.data.message}</Typography.Text>
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  marginLeft: 16
                                }}
                              >
                                <Typography.Text>- Maior ou igual a R$ {currency.mask(condition.min_value?.toFixed(2))} e menor ou igual a R$ {currency.mask(condition.max_value?.toFixed(2))}</Typography.Text>
                              </div>
                            </div>
                          )
                        }
                      </div>

                      {
                        index < conditionsList.length - 1 && <Divider style={{ margin: 0 }} />
                      }
                    </Fragment>
                  ))
                }
              </Col>

              <Col>
                <Button
                  onClick={() => setIsConditionModalVisible(true)}
                  disabled={!valuesFrom.length}
                >
                  Nova condição
                </Button>

                <Button
                  disabled={!conditionsList.length}
                  htmlType="submit"
                  form="comparisonForm"
                  style={{
                    marginLeft: 16,
                  }}
                >
                  Salvar regra
                </Button>
              </Col>
            </Row>
          </Form>
        )
      }

      <Modal
        title="Adicionar condição"
        visible={isConditionModalVisible}
        onCancel={() => setIsConditionModalVisible(false)}
        okButtonProps={{
          form: 'conditionForm',
          htmlType: 'submit',
        }}
      >
        <Form
          ref={conditionFormRef}
          name='conditionForm'
          layout="vertical"
          onFinish={handleAddCondition}
          onSubmitCapture={(e) => e.preventDefault()}
        >
          <Col span={24}>
            <Form.Item
              name="field"
              label="Item"
              rules={[{ required: true, message: 'Por favor, selecione um item' }]}
            >
              <Select
                placeholder="Selecione"
                options={valuesFrom.map((e) => ({
                  label: nodes.find((node) => node.id === e)?.data.message,
                  value: e,
                }))}
                onChange={setSelectedComparisonItem}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              name="type"
              label="Tipo"
              rules={[{ required: true, message: 'Por favor, selecione pelo um tipo' }]}
            >
              <Select
                placeholder="Selecione"
                onChange={setConditionType}
              >
                <Select.Option value="values">Valores</Select.Option>
                <Select.Option value="number_interval">Intervalo numérico</Select.Option>
              </Select>
            </Form.Item>
          </Col>

          {
            conditionType === "values" && (
              <Col span={24}>
                <Form.Item
                  name="values"
                  label="Valores"
                  rules={[{ required: true, message: 'Por favor, selecione pelo um valor' }]}
                >
                  <Select
                    placeholder="Selecione"
                    mode="multiple"
                    options={nodes.find((node) => node.id === selectedComparisonItem)?.data?.options?.map((option) => ({
                      value: option.text,
                      label: option.text,
                    })) || []}
                  />
                </Form.Item>
              </Col>
            )
          }

          {
            conditionType === "number_interval" && (
              <>
                <Col span={24}>
                  <Form.Item
                    name="min_value"
                    label="Valor mínimo"
                    rules={[{ required: true, message: 'Por favor, digite um valor mínimo' }]}
                  >
                    <Input placeholder="Digite o valor mínimo" />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item
                    name="max_value"
                    label="Valor máximo"
                    rules={[{ required: true, message: 'Por favor, digite um valor máximo' }]}
                  >
                    <Input placeholder="Digite o valor máximo" />
                  </Form.Item>
                </Col>
              </>
            )
          }
        </Form>
      </Modal>

      <Modal
        title="Adicionar opção"
        visible={isOptionModalVisible}
        onCancel={() => setIsOptionModalVisible(false)}
        okButtonProps={{
          form: 'optionForm',
          htmlType: 'submit',
        }}
      >
        <Form
          ref={optionFormRef}
          name='optionForm'
          layout="vertical"
          onFinish={handleAddOption}
          onSubmitCapture={(e) => e.preventDefault()}
        >
          <Row gutter={16}>
            <Col span={24} >
              <Form.Item
                name="option"
                label="Opção"
                rules={[
                  {
                    required: true,
                    message: 'Digite a opção',
                  },
                ]}
              >
                <Input placeholder="Digite a opção" />
              </Form.Item>
            </Col>

            <Col span={24} >
              <Form.Item
                name="description"
                label="Descrição"
              >
                <Input placeholder="Digite a descrição" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Drawer >
  )
}